import { ClientEmail } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusIcon, Pencil, Trash2, Mail, ExternalLink } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type EmailsPanelProps = {
  emails: ClientEmail[];
  clientId: number;
};

const normalizeStatus = (status: string | null | undefined): string => status?.toLowerCase() ?? '';

const toDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

export function EmailsPanel({ emails, clientId }: EmailsPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const addEmail = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => apiRequest('POST', '/api/client-emails', { clientId, ...payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/emails`] });
      setIsAddDialogOpen(false);
      toast({ title: 'Email logged' });
    },
    onError: () => toast({ title: 'Failed to log email', variant: 'destructive' })
  });

  const deleteEmail = useMutation({
    mutationFn: async (id: number) => apiRequest('DELETE', `/api/client-emails/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/emails`] });
      setIsDeleteDialogOpen(false);
      setSelectedEmailId(null);
      toast({ title: 'Email deleted' });
    },
    onError: () => toast({ title: 'Failed to delete email', variant: 'destructive' })
  });
  
  const selectedEmail = selectedEmailId 
    ? emails.find(email => email.id === selectedEmailId) 
    : null;

  function handleAddEmail() {
    setIsAddDialogOpen(true);
  }

  function handleViewEmail(email: ClientEmail) {
    setSelectedEmailId(email.id);
    setIsViewDialogOpen(true);
  }

  function handleDeleteEmail(email: ClientEmail) {
    setSelectedEmailId(email.id);
    setIsDeleteDialogOpen(true);
  }
  
  function getEmailStatusBadge(status: string | null | undefined) {
    switch (normalizeStatus(status)) {
      case 'sent':
        return <Badge variant="outline">Sent</Badge>;
      case 'received':
        return <Badge variant="secondary">Received</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Email Communication</h3>
        <Button size="sm" onClick={handleAddEmail}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Log Email
        </Button>
      </div>

      {emails.length === 0 ? (
        <Alert>
          <AlertDescription>
            No email communications have been logged for this contact yet.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {emails.map((email) => (
            <Card key={email.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleViewEmail(email)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <h4 className="font-medium">{email.subject}</h4>
                      {getEmailStatusBadge(email.status)}
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
                            <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => handleViewEmail(email)}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          <span>View Details</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteEmail(email)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>
                      {normalizeStatus(email.status) === 'sent' ? 'To: ' : 'From: '}
                      {(() => {
                        const value = normalizeStatus(email.status) === 'sent' ? email.to : email.from;
                        return value ?? '—';
                      })()}
                    </span>
                    <span>
                      {(() => {
                        const sentAt = toDate(email.sentAt);
                        return sentAt ? format(sentAt, 'MMM d, yyyy') : '—';
                      })()}
                    </span>
                  </div>
                  
                  {email.content && (
                    <p className="mt-2 text-sm line-clamp-2">{email.content}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Email Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log Email Communication</DialogTitle>
            <DialogDescription>
              Record an email sent to or received from this contact.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to log email communications.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Email Dialog */}
      {selectedEmail && (
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-2xl">
              <DialogHeader>
              <DialogTitle>{selectedEmail.subject}</DialogTitle>
              <DialogDescription>
                {(() => {
                  const sentAt = toDate(selectedEmail.sentAt);
                  return sentAt ? format(sentAt, 'MMMM d, yyyy • h:mm a') : '—';
                })()}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <p>{getEmailStatusBadge(selectedEmail.status)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {normalizeStatus(selectedEmail.status) === 'sent' ? 'Recipient' : 'Sender'}
                  </p>
                  <p>
                    {(() => {
                      const value = normalizeStatus(selectedEmail.status) === 'sent'
                        ? selectedEmail.to
                        : selectedEmail.from;
                      return value ?? '—';
                    })()}
                  </p>
                </div>
              </div>
              
              {selectedEmail.content && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Message</p>
                  <div className="border rounded-md p-4 bg-muted/50">
                    <p className="whitespace-pre-wrap text-sm">{selectedEmail.content}</p>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Email Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Email Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this email record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="py-4 border rounded-md p-3 bg-muted/50">
              <p className="font-medium">{selectedEmail.subject}</p>
              <p className="text-sm text-muted-foreground">
                {(() => {
                  const sentAt = toDate(selectedEmail.sentAt);
                  return sentAt ? format(sentAt, 'MMM d, yyyy') : '—';
                })()}
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                if (selectedEmail) {
                  deleteEmail.mutate(selectedEmail.id);
                }
              }}
            >
              {deleteEmail.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
